import React, { useState, useEffect } from 'react';
import { XMarkIcon, Cog6ToothIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { dspApi } from '../../repositories/dsp';
import Button from '../ui/Button';
import { Skeleton, SkeletonText } from '../ui/skeleton';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoToSettings: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  isOpen,
  onClose,
  onGoToSettings
}) => {
  const [connectedDSPs, setConnectedDSPs] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      checkDSPConnections();
    }
  }, [isOpen]);

  const checkDSPConnections = async () => {
    try {
      const response = await dspApi.getDSPs();
      setConnectedDSPs(response.data.data?.length || 0);
    } catch (error) {
      setConnectedDSPs(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyPolicyClick = () => {
    window.open('/privacy-policy', '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md sm:max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <Cog6ToothIcon className="w-6 h-6 text-primary-700" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-black">
                Welcome to Cadence IO!
              </h2>
              <p className="text-sm text-neutral-600">Let's get you set up</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-100 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5 text-neutral-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {loading ? (
            // Which message applies depends on the DSP count; until it is known,
            // a placeholder rather than flashing the "connect your DSPs" copy.
            <div className="space-y-4" aria-busy="true">
              <Skeleton className="w-56 h-5" />
              <SkeletonText lines={4} />
              <Skeleton className="w-full h-20 rounded-lg" />
              <Skeleton className="w-full h-10" />
              <Skeleton className="w-full h-10" />
            </div>
          ) : connectedDSPs > 0 ? (
            // User already has DSP connections
            <div className="text-center">
              <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-8 h-8 text-success-600" />
              </div>
              <h3 className="text-lg font-medium text-black mb-2">
                You're All Set!
              </h3>
              <p className="text-sm text-neutral-600 mb-6">
                You already have {connectedDSPs} DSP account{connectedDSPs !== 1 ? 's' : ''} connected. 
                You can manage your connections or add more in the settings.
              </p>
              <div className="space-y-3">
                <Button
                  onClick={onGoToSettings}
                  className="w-full sm:text-base"
                >
                  Manage DSP Settings
                </Button>
                <Button
                  variant="neutral"
                  onClick={onClose}
                  className="w-full sm:text-base"
                >
                  Continue to Dashboard
                </Button>
              </div>
            </div>
          ) : (
            // User needs to set up DSP connections
            <>
              <div className="mb-6">
                <h3 className="text-base sm:text-lg font-medium text-black mb-3">
                  Connect Your DSP Accounts
                </h3>
                <p className="text-sm sm:text-base text-neutral-600 leading-relaxed mb-4">
                  To get the most out of Cadence IO, we recommend connecting your Digital Service Provider (DSP) accounts. This will help us:
                </p>
                <ul className="list-disc ml-5 space-y-1 text-sm sm:text-base text-neutral-600">
                  <li>Fetch your streaming data automatically</li>
                  <li>Improve pitch targeting accuracy</li>
                  <li>Provide better analytics and insights</li>
                  <li>Streamline your workflow</li>
                </ul>
              </div>

              <div className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-primary-700 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-primary-900 mb-1">
                      Secure & Private
                    </p>
                    <p className="text-sm text-primary-800">
                      Your login credentials are encrypted and stored securely. You can{' '}
                      <Button variant="link" size="inline" onClick={handlePrivacyPolicyClick} className="underline">
                        review our privacy policy
                      </Button>
                      {' '}to learn more about how we protect your data.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={onGoToSettings}
                  className="w-full sm:text-base"
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                  Connect DSP Accounts
                </Button>
                
                <Button
                  variant="neutral"
                  onClick={onClose}
                  className="w-full sm:text-base"
                >
                  Skip for Now
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-neutral-50 rounded-b-xl border-t border-neutral-100">
          <p className="text-xs sm:text-sm text-neutral-500 text-center">
            You can always connect your DSP accounts later in Settings → DSPs
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;